import React, {useMemo} from 'react';

import {
  LightText,
  MediumText,
  RegularText,
  SemiBoldText,
} from 'shared/components/text';

import {CartProductProps} from 'modules/cart/types';
import {AdditionalGroupConfigTypeEnum} from 'modules/products/types';
import Column from 'shared/components/column';
import Row from 'shared/components/row';
import useProductDetails from 'modules/products/hooks/use-product-details';
import isNumber from 'shared/util/is-number';
import styled, {useTheme} from 'styled-components';
import {IoAddOutline, IoRemoveOutline} from 'react-icons/io5';
import toBrl from 'shared/util/to-brl';
import {errorNotification} from 'shared/services/notification';
import saleBanner from 'shared/assets/images/sale-banner.png';
import {useNavigate} from 'react-router-dom';

interface Props extends CartProductProps {
  index: number;
  removeProduct: () => void;
  incrementQuantity: () => void;
  decrementQuantity: () => void;
  products?: CartProductProps[];
}

const Product: React.FC<Props> = ({
  index,
  incrementQuantity,
  decrementQuantity,
  removeProduct,
  products = [],
  ...cartProduct
}) => {
  const {
    id,
    quantity,
    size,
    additionalGroups,
    observation,
    saleId,
    onePerOrder,
    companyId,
  } = cartProduct;
  const {data} = useProductDetails(id);
  const {colors} = useTheme();
  const navigate = useNavigate();

  const isRemainingProductOnSale = useMemo(() => {
    if (!saleId) return true;
    if (!data) return true;

    const remainingProducts =
      data.remainingProducts ??
      data.sizes?.find((size) => size.saleId === saleId)?.remainingProducts;

    if (!isNumber(remainingProducts)) return true;

    const cartSaleQuantity = products.reduce(
      (sum, someProduct) =>
        someProduct.saleId && someProduct.saleId === saleId
          ? sum + someProduct.quantity
          : sum,
      0,
    );

    return (remainingProducts ?? 0) > cartSaleQuantity;
  }, [products, data, saleId]);

  if (!data) return null;

  const {name, price, priceMode, additionalGroupsConfig, salePrice, image} =
    data;

  const getAdditionalsPrice = () =>
    Object.keys(additionalGroups ?? {}).reduce(
      (additionalsTotal, additionalGroupId) => {
        const currentAdditionalGroupsConfig = additionalGroupsConfig.find(
          (additionalGroupConfig) =>
            additionalGroupConfig.id === additionalGroupId,
        );

        if (!currentAdditionalGroupsConfig) return additionalsTotal;

        const {additionalGroup, type, isFree, additionalPricesWhenFree} =
          currentAdditionalGroupsConfig;

        const additionals = additionalGroups![additionalGroupId];

        let additionalsPrice = additionals.reduce((total, {id, quantity}) => {
          const additional = additionalGroup.additionals.find(
            (someAdditional) => someAdditional.id === id,
          );

          if (!additional) return total;

          const chargeWhenFreePrice =
            additionalPricesWhenFree?.find((additional) => additional.id === id)
              ?.price ?? 0;

          const price = !isFree ? additional.price : chargeWhenFreePrice;

          return total + price * quantity;
        }, 0);

        if (type === AdditionalGroupConfigTypeEnum.highest_value)
          additionalsPrice = additionals.reduce((highestValue, {id}) => {
            const additional = additionalGroup.additionals.find(
              (someAdditional) => someAdditional.id === id,
            );

            if (!additional) return highestValue;

            const chargeWhenFreePrice =
              additionalPricesWhenFree?.find(
                (additional) => additional.id === id,
              )?.price ?? 0;

            const price = !isFree ? additional.price : chargeWhenFreePrice;

            return price > highestValue ? price : highestValue;
          }, 0);

        if (type === AdditionalGroupConfigTypeEnum.average) {
          const quantity = additionals.reduce(
            (total, cartAdditional) => total + cartAdditional.quantity,
            0,
          );
          additionalsPrice = additionalsPrice / quantity;
        }

        return additionalsPrice + additionalsTotal;
      },
      0,
    );

  const getPriceWithSale = () => {
    const isSalePrice = isNumber(salePrice);
    const isSizeSalePrice = isNumber(size?.salePrice);

    if ((!isSalePrice && !isSizeSalePrice) || !saleId) return;

    const productPrice =
      priceMode === 'fixed'
        ? isSalePrice
          ? salePrice!
          : price
        : isSizeSalePrice
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          size?.salePrice!
        : size?.price ?? 0;

    return (productPrice + getAdditionalsPrice()) * quantity;
  };

  const getPrice = () => {
    const productPrice = priceMode === 'fixed' ? price : size?.price ?? 0;

    return (productPrice + getAdditionalsPrice()) * quantity;
  };

  const total = getPrice();
  const totalWithSale = getPriceWithSale();

  return (
    <div
      onClick={() =>
        navigate(`${data?.companyId}/products/${id}`, {
          state: {index, cartProduct: JSON.stringify(cartProduct)},
        })
      }>
      <Row justifyContent="space-between">
        <Row
          gap={10}
          style={{flexShrink: 1}}
          alignItems="flex-start"
          onClick={() => ''}>
          <div
            style={{
              width: 65,
              height: 65,
              borderRadius: 8,
              overflow: 'hidden',
            }}>
            {image && (
              <img
                style={{objectFit: 'cover', width: '100%', height: '100%'}}
                src={image}
              />
            )}
            {!image && (
              <img
                style={{objectFit: 'cover', width: '100%', height: '100%'}}
                src={saleBanner}
              />
            )}
          </div>
          <Column gap={10}>
            <Column>
              <RegularText>
                {name}
                {size ? ` - ${size.name}` : ''}
              </RegularText>
              {!totalWithSale && <MediumText>{toBrl(total)}</MediumText>}
              {totalWithSale && (
                <Row gap={5}>
                  <MediumText color="green">{toBrl(totalWithSale!)}</MediumText>
                  <MediumText
                    color="opaqueText"
                    size="small"
                    style={{textDecorationLine: 'line-through'}}>
                    {toBrl(total)}
                  </MediumText>
                </Row>
              )}
            </Column>
            <Column gap={5}>
              {Object.keys(additionalGroups ?? {}).map((additionalGroupId) => {
                const currentAdditionalGroupsConfig =
                  additionalGroupsConfig.find(
                    (additionalGroupConfig) =>
                      additionalGroupConfig.id === additionalGroupId,
                  );

                if (!currentAdditionalGroupsConfig) return;

                const {additionalGroup} = currentAdditionalGroupsConfig;

                const additionals = additionalGroups![additionalGroupId];

                const productAdditionals = additionals.reduce(
                  (productAdditionals, additional) => {
                    const isAdditional = additionalGroup.additionals.find(
                      (someAdditional) => additional.id === someAdditional.id,
                    );

                    return [
                      ...productAdditionals,
                      {...isAdditional, quantity: additional.quantity},
                    ];
                  },
                  [] as any[],
                );

                return productAdditionals.map((productAdditional, index) => (
                  <Row key={index} gap={5}>
                    <Number>
                      <RegularText size="small">
                        {productAdditional.quantity}
                      </RegularText>
                    </Number>
                    <RegularText size="small">
                      {productAdditional.name}
                    </RegularText>
                  </Row>
                ));
              })}
              {observation && (
                <LightText size="small">Observação: {observation}</LightText>
              )}
            </Column>
          </Column>
        </Row>
        <Quantity>
          <QuantityButton onClick={decrementQuantity}>
            <IoRemoveOutline color={colors.primary} />
          </QuantityButton>
          <SemiBoldText>{quantity}</SemiBoldText>
          <QuantityButton
            onClick={() =>
              onePerOrder
                ? errorNotification({
                    title: 'Erro ao alterar quantidade',
                    message: 'Esse item é limitado a um uso na promoção!',
                  })
                : isRemainingProductOnSale
                ? incrementQuantity()
                : errorNotification({
                    title: 'Erro ao alterar quantidade',
                    message: 'Limite máximo de itens da promoção alcançado!',
                  })
            }>
            <IoAddOutline color={colors.primary} />
          </QuantityButton>
        </Quantity>
      </Row>
    </div>
  );
};

export default Product;

export const Number = styled.div`
  background-color: #ebebeb;
  padding: 2px 5px; /* vertical 2px, horizontal 5px */
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Quantity = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid ${(props) => props.theme.colors.softBackground};
  border-radius: 5px;
  align-items: center;
  gap: 10px;
`;

export const QuantityButton = styled.button`
  padding: 10px;
  border: none;
  background: none;
  cursor: pointer;
`;
